import React, {useContext, useEffect, useRef, useState} from 'react';
import Breadcrumb from '@article-overview/components/header/Breadcrumb.jsx';
import FilterOrderingButton from '@article-overview/components/header/FilterOrderingButton.jsx';
import FilterContainer from '@article-overview/components/header/FilterContainer.jsx';
import {useBreakpointUp} from 'bonprix-design-system';
import {useStickyOffset} from '@article-overview/components/hooks/use-sticky-offset.js';
import {articleOverviewStaticContext} from '@article-overview/components/ArticleOverviewStatic.jsx';
import {DefaultFallback} from '@shopping-funnel/scripts/components/fallbacks/DefaultFallback.jsx';
import useScrollDirection from '@article-overview/hooks/useScrollDirection.js';
import {useBreakpointDown} from '@bonprix/design-system';
import Device from '@core/scripts/components/device.js';
import FilterTokens from '@article-overview/components/header/FilterTokens.tsx';

export default function OverviewHeader({
    shouldRenderBreadcrumbs,
    breadcrumbs,
    hideBreadcrumbBack = false,
    landmarkContent,
    filterContent,
    ordering,
    filledArticleList,
    noArticlesError,
}) {
    const {filterRootShopId} = useContext(articleOverviewStaticContext);
    const isMobile = useBreakpointDown('m');
    const breakpointLUp = useBreakpointUp('l');
    const breakpointXLUp = useBreakpointUp('xl');
    const {stickyElementRef, offsetTop} = useStickyOffset();
    const scrollDirection = useScrollDirection(100);
    const [isSticky, setIsSticky] = useState(false);
    const [hasAnimated, setHasAnimated] = useState(false);
    const wrapperRef = useRef(null);
    const isApp = Device.isApp();
    const rootShopName = breadcrumbs?.items.find((item) => item.shopId === filterRootShopId.current)?.name;
    const shouldAnimateHeader = breakpointXLUp && scrollDirection === 'up';
    const showOnlyTokens = !filledArticleList && !noArticlesError;
    const sentinelRef = useRef(null);
    const showHeaderRow = shouldRenderBreadcrumbs || breakpointLUp;

    // Detect when element becomes sticky
    useEffect(() => {
        if (!sentinelRef.current || !breakpointXLUp) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                const currentlySticky = entry.intersectionRatio < 1;
                setIsSticky(currentlySticky);

                if (currentlySticky && !hasAnimated) {
                    setHasAnimated(true);
                }
            },
            {
                threshold: [1],
                rootMargin: '-1px 0px 0px 0px'
            }
        );

        observer.observe(sentinelRef.current);

        return () => observer.disconnect();
    }, [breakpointXLUp]);

    const classNames = [
        'article-overview__sticky-header-wrapper',
        shouldAnimateHeader ? '-should-animate' : '',
        isSticky ? '-is-sticky' : '',
        hasAnimated && isSticky ? '-animate-on-stick' : ''
    ].filter(Boolean).join(' ');

    return (
        <>
            <div ref={sentinelRef} style={{height: '1px', marginBottom: '-1px'}} aria-hidden="true" />

            <div ref={wrapperRef} className={classNames}>
                {showHeaderRow && (
                    <div className="article-overview__header" ref={stickyElementRef}>
                        {shouldRenderBreadcrumbs && <Breadcrumb breadcrumbs={breadcrumbs} hideBreadcrumbBack={hideBreadcrumbBack} />}
                        {breakpointLUp && (
                            <div className="article-overview__header-filter-btn">
                                <FilterOrderingButton filterViewValue={landmarkContent?.filterViewLandmark} />
                            </div>
                        )}
                    </div>
                )}
                <DefaultFallback>
                    <FilterContainer
                        filterContent={filterContent}
                        landmarkContent={landmarkContent}
                        ordering={ordering}
                        filledArticleList={filledArticleList}
                        showOnlyTokens={!filledArticleList && !noArticlesError}
                        rootShopName={rootShopName}
                        subshopsTopOffset={offsetTop}
                    />
                </DefaultFallback>
            </div>
            {!breakpointLUp && !showOnlyTokens && (
                <div className={`article-overview__sticky-mobile-filter-wrapper${isApp ? ' -is-app' : ''}`}>
                    <DefaultFallback>
                        <FilterOrderingButton filterViewValue={landmarkContent?.filterViewLandmark} />
                    </DefaultFallback>
                </div>
            )}
            <DefaultFallback>
                <FilterTokens filterTokens={filterContent.filterTokens} />
            </DefaultFallback>
        </>
    );
}
